import { useCallback } from "react";
import { Trash2, FileSpreadsheet, Calendar, Clock, Loader2 } from "lucide-react";
import type { ImportBatchType, ImportBatchSummary } from "@bookdental/schemas";
import { Button, toast } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";

type Props = {
  type: ImportBatchType;
};

export function ImportHistoryList({ type }: Props) {
  const confirm = useConfirm();
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.import.listBatches.useQuery({ type });
  const deleteMutation = trpc.import.deleteBatch.useMutation({
    onSuccess: () => {
      utils.import.listBatches.invalidate();
      if (type === "PATIENTS") utils.patient.list.invalidate();
      else utils.appointment.listByRange.invalidate();
    },
  });

  const handleDelete = useCallback(
    async (batch: ImportBatchSummary) => {
      const ok = await confirm({
        title: "Apagar banco de dados importado?",
        description: (
          <div className="space-y-2 text-sm">
            <p>
              Essa acao vai remover{" "}
              <strong>
                {batch.liveCount}{" "}
                {type === "PATIENTS" ? "paciente(s)" : "consulta(s)"}
              </strong>{" "}
              que foram criados por essa importacao.
            </p>
            <p className="text-xs text-zinc-500">
              Registros que foram apenas atualizados pela importacao nao serao removidos.
              Esta acao e irreversivel.
            </p>
          </div>
        ),
        confirmText: "Apagar definitivamente",
        cancelText: "Cancelar",
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteMutation.mutateAsync({ id: batch.id });
        toast.success("Importacao removida.");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Erro ao apagar");
      }
    },
    [confirm, deleteMutation, type],
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 px-4 py-3 text-xs text-zinc-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Carregando historico...
      </div>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white">
      <div className="border-b border-zinc-100 px-4 py-2.5">
        <p className="text-xs font-semibold text-zinc-900">Importacoes realizadas</p>
        <p className="text-[11px] text-zinc-500">
          {data.length} importacao(oes) — apagar remove os registros criados por ela.
        </p>
      </div>
      <ul className="divide-y divide-zinc-100">
        {data.map((b) => (
          <BatchRow
            key={b.id}
            batch={b}
            onDelete={() => handleDelete(b)}
            deleting={deleteMutation.isPending && deleteMutation.variables?.id === b.id}
          />
        ))}
      </ul>
    </div>
  );
}

function BatchRow({
  batch,
  onDelete,
  deleting,
}: {
  batch: ImportBatchSummary;
  onDelete: () => void;
  deleting: boolean;
}) {
  const date = new Date(batch.createdAt);
  const dateStr = date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
        <FileSpreadsheet className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-zinc-900">
          {batch.fileName || "Importacao sem nome de arquivo"}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {dateStr}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {timeStr}
          </span>
          <span className="text-zinc-300">•</span>
          <span>
            <strong className="text-emerald-700">{batch.createdCount}</strong> criados
          </span>
          {batch.updatedCount > 0 && (
            <span>
              <strong className="text-brand-700">{batch.updatedCount}</strong> atualizados
            </span>
          )}
          {batch.skippedCount > 0 && (
            <span>
              <strong className="text-amber-700">{batch.skippedCount}</strong> pulados
            </span>
          )}
          {batch.errorCount > 0 && (
            <span>
              <strong className="text-red-700">{batch.errorCount}</strong> erros
            </span>
          )}
          <span className="text-zinc-300">•</span>
          <span>
            <strong className="text-zinc-700">{batch.liveCount}</strong> ainda no banco
          </span>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onDelete}
        disabled={deleting || batch.liveCount === 0}
        className="shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
      >
        {deleting ? (
          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Trash2 className="mr-1 h-3.5 w-3.5" />
        )}
        Apagar
      </Button>
    </li>
  );
}
